// frontend/src/samkalp-admin/tabs/CurrentAffairsTab.js
//
// Duplicated from frontend/src/pages/AskOSAdmin.js's CurrentAffairsTab (not exported there).
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Form, Button, Alert, Badge, Spinner, Modal } from 'react-bootstrap';
import { Plus, Pencil, Trash2, ExternalLink } from 'lucide-react';
import api from '../../api/api';
import { useAlert } from './shared';

const emptyFeedForm = { name: '', url: '', source_label: '', poll_interval_min: '60', is_active: true };

const CurrentAffairsTab = ({ slug }) => {
    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [refreshingId, setRefreshingId] = useState(null);
    const [editFeed, setEditFeed] = useState(null);
    const [form, setForm] = useState(emptyFeedForm);
    const [alert, showAlert] = useAlert();

    const loadFeeds = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/tenant/${slug}/askos/admin/current-affairs/feeds`);
            setFeeds(res.data);
        } catch { /* empty */ }
        setLoading(false);
    }, [slug]);

    useEffect(() => { loadFeeds(); }, [loadFeeds]);

    const openCreate = () => { setForm(emptyFeedForm); setEditFeed({}); };
    const openEdit = (feed) => {
        setForm({
            name: feed.name,
            url: feed.url,
            source_label: feed.source_label,
            poll_interval_min: String(feed.poll_interval_min),
            is_active: feed.is_active,
        });
        setEditFeed(feed);
    };

    const saveFeed = async () => {
        setSaving(true);
        const payload = {
            name: form.name,
            url: form.url,
            source_label: form.source_label,
            poll_interval_min: parseInt(form.poll_interval_min, 10) || 60,
            is_active: form.is_active,
        };
        try {
            if (editFeed && editFeed.id) {
                await api.patch(`/tenant/${slug}/askos/admin/current-affairs/feeds/${editFeed.id}`, payload);
                showAlert('Feed updated.');
            } else {
                await api.post(`/tenant/${slug}/askos/admin/current-affairs/feeds`, payload);
                showAlert('Feed created.');
            }
            setEditFeed(null);
            loadFeeds();
        } catch (e) {
            showAlert(e.response?.data?.detail || 'Save failed.', 'danger');
        }
        setSaving(false);
    };

    const deleteFeed = async (feed) => {
        try {
            await api.delete(`/tenant/${slug}/askos/admin/current-affairs/feeds/${feed.id}`);
            showAlert('Feed deleted.', 'warning');
            loadFeeds();
        } catch (e) {
            showAlert(e.response?.data?.detail || 'Delete failed.', 'danger');
        }
    };

    const refreshFeed = async (feed) => {
        setRefreshingId(feed.id);
        try {
            const res = await api.post(`/tenant/${slug}/askos/admin/current-affairs/feeds/${feed.id}/refresh`, {});
            if (res.data.error) {
                showAlert(`Refresh failed: ${res.data.error}`, 'danger');
            } else {
                showAlert(`Inserted ${res.data.inserted}, updated ${res.data.updated}.`);
            }
            loadFeeds();
        } catch (e) {
            showAlert(e.response?.data?.detail || 'Refresh failed.', 'danger');
        }
        setRefreshingId(null);
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>;

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small">{alert.msg}</Alert>}

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">Current Affairs Feeds</h6>
                <Button size="sm" className="rounded-pill px-3" onClick={openCreate}>
                    <Plus size={14} className="me-1" /> Add Feed
                </Button>
            </div>

            <Card className="border-0 shadow-sm">
                <Table hover responsive className="mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>Name</th>
                            <th>Source</th>
                            <th>Interval</th>
                            <th>Status</th>
                            <th>Last Polled</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {feeds.map(f => (
                            <tr key={f.id}>
                                <td>
                                    <div className="fw-semibold x-small">{f.name}</div>
                                    <div className="text-muted x-small text-truncate" style={{ maxWidth: 280 }}>{f.url}</div>
                                </td>
                                <td><Badge bg="light" text="dark" className="border">{f.source_label}</Badge></td>
                                <td className="x-small">{f.poll_interval_min} min</td>
                                <td>
                                    {!f.is_active
                                        ? <Badge bg="secondary" bg-opacity="10">disabled</Badge>
                                        : f.last_status === 'error'
                                            ? <Badge bg="danger" title={f.last_error}>error</Badge>
                                            : <Badge bg="success">ok</Badge>}
                                </td>
                                <td className="x-small text-muted">{f.last_polled_at ? new Date(f.last_polled_at).toLocaleString() : 'never'}</td>
                                <td className="text-end">
                                    <Button variant="link" size="sm" className="p-1" title="Refresh now"
                                        onClick={() => refreshFeed(f)} disabled={refreshingId === f.id}>
                                        {refreshingId === f.id ? <Spinner animation="border" size="sm" /> : <ExternalLink size={14} />}
                                    </Button>
                                    <Button variant="link" size="sm" className="p-1" title="Edit" onClick={() => openEdit(f)}>
                                        <Pencil size={14} />
                                    </Button>
                                    <Button variant="link" size="sm" className="p-1 text-danger" title="Delete" onClick={() => deleteFeed(f)}>
                                        <Trash2 size={14} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {feeds.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-muted py-4">No feeds configured yet.</td></tr>
                        )}
                    </tbody>
                </Table>
            </Card>

            <Modal show={!!editFeed} onHide={() => setEditFeed(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6 fw-bold">{editFeed?.id ? 'Edit Feed' : 'Add Feed'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Name</Form.Label>
                        <Form.Control value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. PIB — Releases (ModId 6)" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">RSS URL</Form.Label>
                        <Form.Control value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://…" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Source Label</Form.Label>
                        <Form.Control value={form.source_label} onChange={e => setForm(f => ({ ...f, source_label: e.target.value }))} placeholder="e.g. PIB" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Poll Interval (minutes)</Form.Label>
                        <Form.Control type="number" value={form.poll_interval_min} onChange={e => setForm(f => ({ ...f, poll_interval_min: e.target.value }))} />
                    </Form.Group>
                    <Form.Check type="checkbox" label="Active" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={() => setEditFeed(null)}>Cancel</Button>
                    <Button variant="primary" className="rounded-pill px-4" onClick={saveFeed} disabled={saving || !form.name || !form.url || !form.source_label}>
                        {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : 'Save Feed'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CurrentAffairsTab;
